/**
 * FormContext - React Context for Form State Management
 *
 * Provides form state (values, errors, touched), input registration,
 * validation, and workflow submission capabilities.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import {
  FormContextValue,
  FormState,
  INITIAL_FORM_STATE,
  FormContainerProperties,
  WorkflowInputSchema,
  SchemaDefinition,
  FormSubmissionResult,
  WorkflowVariable,
  SchemaField,
} from "@/types/form.types";

// ============================================================================
// Context
// ============================================================================

const FormContext = createContext<FormContextValue | null>(null);

// ============================================================================
// Hook to consume form context
// ============================================================================

/**
 * Hook to access form context. Returns null if not inside a FormContainer.
 */
export function useFormContext(): FormContextValue | null {
  return useContext(FormContext);
}

/**
 * Hook that throws if not inside a FormContainer (for required form binding)
 */
export function useRequiredFormContext(): FormContextValue {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error(
      "useRequiredFormContext must be used within a FormContextProvider"
    );
  }
  return context;
}

// ============================================================================
// Provider Props
// ============================================================================

interface FormContextProviderProps {
  formId: string;
  properties: FormContainerProperties;
  children: ReactNode;
  onSubmitSuccess?: (result: FormSubmissionResult) => void;
  onSubmitError?: (error: Error) => void;
}

// ============================================================================
// Provider Implementation
// ============================================================================

export const FormContextProvider: React.FC<FormContextProviderProps> = ({
  formId,
  properties,
  children,
  onSubmitSuccess,
  onSubmitError,
}) => {
  // Form state
  const [state, setState] = useState<FormState>(INITIAL_FORM_STATE);

  // Schema/workflow info
  const [workflowSchema, setWorkflowSchema] =
    useState<WorkflowInputSchema | null>(null);
  const [schemaDefinition, setSchemaDefinition] =
    useState<SchemaDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Input registration map (fieldName -> inputId)
  const [registeredInputs, setRegisteredInputs] = useState<
    Map<string, string>
  >(new Map());

  // Track fetch attempts to prevent infinite retry loops
  const schemaFetchAttemptedRef = useRef<string | null>(null);
  const workflowFetchAttemptedRef = useRef<string | null>(null);

  // ========================================================================
  // Load workflow schema when workflowId changes
  // ========================================================================
  useEffect(() => {
    const loadWorkflowSchema = async () => {
      if (!properties.workflowId) {
        setWorkflowSchema(null);
        return;
      }

      // Prevent re-fetching if we've already attempted this workflow
      if (workflowFetchAttemptedRef.current === properties.workflowId) {
        return;
      }
      workflowFetchAttemptedRef.current = properties.workflowId;

      setIsLoading(true);
      try {
        // Import dynamically to avoid circular dependencies
        const { workflowApi } = await import("@/services/api/workflowApi");
        const response = await workflowApi.get<{
          success: boolean;
          workflow: {
            _id: string;
            name: string;
            definition?: {
              variables?: Array<{
                name: string;
                type: string;
                required?: boolean;
                defaultValue?: unknown;
                description?: string;
              }>;
            };
          };
        }>(`/api/workflows/${properties.workflowId}`);

        if (response.success && response.workflow) {
          const variables: WorkflowVariable[] = (
            response.workflow.definition?.variables || []
          ).map((v) => ({
            name: v.name,
            type: (v.type?.toLowerCase() || "string") as WorkflowVariable["type"],
            required: v.required ?? false,
            defaultValue: v.defaultValue,
            description: v.description,
          }));

          setWorkflowSchema({
            workflowId: properties.workflowId,
            workflowName: response.workflow.name,
            variables,
          });

          // Initialize default values
          const defaultValues: Record<string, unknown> = {};
          variables.forEach((v) => {
            if (v.defaultValue !== undefined) {
              defaultValues[v.name] = v.defaultValue;
            }
          });
          if (Object.keys(defaultValues).length > 0) {
            setState((prev) => ({
              ...prev,
              values: { ...defaultValues, ...prev.values },
            }));
          }
        }
      } catch (error) {
        console.error("Failed to load workflow schema:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkflowSchema();
  }, [properties.workflowId]);

  // ========================================================================
  // Load schema definition when schemaId changes (for edit mode)
  // ========================================================================
  useEffect(() => {
    const loadSchemaDefinition = async () => {
      if (!properties.schemaId) {
        setSchemaDefinition(null);
        return;
      }

      // Prevent re-fetching if we've already attempted this schema
      if (schemaFetchAttemptedRef.current === properties.schemaId) {
        return;
      }
      schemaFetchAttemptedRef.current = properties.schemaId;

      setIsLoading(true);
      try {
        const { schemaApi } = await import("@/services/api/schemaApi");
        const schema = await schemaApi.getSchema(properties.schemaId);

        const definition: SchemaDefinition = {
          id: schema.id,
          name: schema.name,
          displayName: schema.displayName,
          description: schema.description,
          status:
            schema.status === "published"
              ? "PUBLISHED"
              : schema.status === "draft"
              ? "DRAFT"
              : "ARCHIVED",
          fields: schema.fields.map((f) => ({
            name: f.name,
            displayName: f.name,
            type: f.type.toUpperCase() as SchemaField["type"],
            required: f.required,
            description: f.description,
            constraints: f.constraints,
          })),
        };

        setSchemaDefinition(definition);
      } catch (error) {
        console.error("Failed to load schema definition:", error);
        // Don't retry on error - the ref will prevent future attempts
      } finally {
        setIsLoading(false);
      }
    };

    loadSchemaDefinition();
  }, [properties.schemaId]);

  // ========================================================================
  // Load record data in edit mode
  // ========================================================================
  useEffect(() => {
    const loadRecordData = async () => {
      if (
        properties.mode !== "edit" ||
        !properties.recordId ||
        !properties.schemaId ||
        !properties.groupId
      ) {
        return;
      }

      setIsLoading(true);
      try {
        const { dataApi } = await import("@/services/api/dataApi");
        const record = await dataApi.getRecord(
          properties.groupId,
          properties.recordId
        );

        if (record) {
          setState((prev) => ({
            ...prev,
            values: record.data || record,
            isDirty: false,
          }));
        }
      } catch (error) {
        console.error("Failed to load record data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecordData();
  }, [
    properties.mode,
    properties.recordId,
    properties.schemaId,
    properties.groupId,
  ]);

  // ========================================================================
  // Input Registration
  // ========================================================================
  const registerInput = useCallback((fieldName: string, inputId: string) => {
    setRegisteredInputs((prev) => {
      const next = new Map(prev);
      next.set(fieldName, inputId);
      return next;
    });
  }, []);

  const unregisterInput = useCallback((fieldName: string) => {
    setRegisteredInputs((prev) => {
      const next = new Map(prev);
      next.delete(fieldName);
      return next;
    });
  }, []);

  // ========================================================================
  // Value Management
  // ========================================================================
  const setValue = useCallback(
    (fieldName: string, value: unknown) => {
      setState((prev) => {
        const newValues = { ...prev.values, [fieldName]: value };

        // Clear error if validateOnChange is enabled
        let newErrors = prev.errors;
        if (properties.validateOnChange) {
          const error = validateFieldInternal(fieldName, value);
          if (error) {
            newErrors = { ...prev.errors, [fieldName]: error };
          } else {
            newErrors = { ...prev.errors };
            delete newErrors[fieldName];
          }
        }

        return {
          ...prev,
          values: newValues,
          errors: newErrors,
          isDirty: true,
          isValid: Object.keys(newErrors).length === 0,
        };
      });
    },
    [properties.validateOnChange]
  );

  const getValue = useCallback(
    (fieldName: string): unknown => {
      return state.values[fieldName];
    },
    [state.values]
  );

  const setTouched = useCallback(
    (fieldName: string) => {
      setState((prev) => {
        const newTouched = { ...prev.touched, [fieldName]: true };

        // Validate on blur if enabled
        let newErrors = prev.errors;
        if (properties.validateOnBlur) {
          const error = validateFieldInternal(fieldName, prev.values[fieldName]);
          if (error) {
            newErrors = { ...prev.errors, [fieldName]: error };
          } else {
            newErrors = { ...prev.errors };
            delete newErrors[fieldName];
          }
        }

        return {
          ...prev,
          touched: newTouched,
          errors: newErrors,
          isValid: Object.keys(newErrors).length === 0,
        };
      });
    },
    [properties.validateOnBlur]
  );

  // ========================================================================
  // Validation
  // ========================================================================
  const validateFieldInternal = useCallback(
    (fieldName: string, value: unknown): string | null => {
      // Get field definition from workflow schema or data schema
      let fieldDef:
        | { required: boolean; type: string; constraints?: Record<string, unknown> }
        | undefined;

      if (workflowSchema) {
        const variable = workflowSchema.variables.find(
          (v) => v.name === fieldName
        );
        if (variable) {
          fieldDef = {
            required: variable.required,
            type: variable.type,
            constraints: variable.constraints,
          };
        }
      }

      if (!fieldDef && schemaDefinition) {
        const field = schemaDefinition.fields.find((f) => f.name === fieldName);
        if (field) {
          fieldDef = {
            required: field.required,
            type: field.type.toLowerCase(),
            constraints: field.constraints,
          };
        }
      }

      if (!fieldDef) {
        return null; // No definition found, skip validation
      }

      // Required check
      if (
        fieldDef.required &&
        (value === undefined || value === null || value === "")
      ) {
        return `${fieldName} is required`;
      }

      // Skip further validation if empty and not required
      if (value === undefined || value === null || value === "") {
        return null;
      }

      // Type validation
      const constraints = fieldDef.constraints || {};

      if (fieldDef.type === "number" || fieldDef.type === "NUMBER") {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return `${fieldName} must be a number`;
        }
        if (constraints.min !== undefined && numValue < (constraints.min as number)) {
          return `${fieldName} must be at least ${constraints.min}`;
        }
        if (constraints.max !== undefined && numValue > (constraints.max as number)) {
          return `${fieldName} must be at most ${constraints.max}`;
        }
      }

      if (fieldDef.type === "string" || fieldDef.type === "STRING") {
        const strValue = String(value);
        if (
          constraints.minLength !== undefined &&
          strValue.length < (constraints.minLength as number)
        ) {
          return `${fieldName} must be at least ${constraints.minLength} characters`;
        }
        if (
          constraints.maxLength !== undefined &&
          strValue.length > (constraints.maxLength as number)
        ) {
          return `${fieldName} must be at most ${constraints.maxLength} characters`;
        }
        if (constraints.pattern) {
          try {
            const regex = new RegExp(constraints.pattern as string);
            if (!regex.test(strValue)) {
              return `${fieldName} format is invalid`;
            }
          } catch {
            // Invalid regex, skip pattern validation
          }
        }
        // Only validate enum if it's a non-empty array
        if (constraints.enum && Array.isArray(constraints.enum) && constraints.enum.length > 0) {
          if (!constraints.enum.includes(strValue)) {
            return `${fieldName} must be one of: ${(constraints.enum as string[]).join(", ")}`;
          }
        }
      }

      return null;
    },
    [workflowSchema, schemaDefinition]
  );

  const validateField = useCallback(
    (fieldName: string): string | null => {
      const error = validateFieldInternal(fieldName, state.values[fieldName]);

      if (error) {
        setState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [fieldName]: error },
          isValid: false,
        }));
      } else {
        setState((prev) => {
          const newErrors = { ...prev.errors };
          delete newErrors[fieldName];
          return {
            ...prev,
            errors: newErrors,
            isValid: Object.keys(newErrors).length === 0,
          };
        });
      }

      return error;
    },
    [validateFieldInternal, state.values]
  );

  const validateForm = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Validate all registered fields
    registeredInputs.forEach((_inputId, fieldName) => {
      const error = validateFieldInternal(fieldName, state.values[fieldName]);
      if (error) {
        errors[fieldName] = error;
      }
    });

    // Also validate all workflow variables (may not have inputs yet)
    if (workflowSchema) {
      workflowSchema.variables.forEach((v) => {
        if (!errors[v.name]) {
          const error = validateFieldInternal(v.name, state.values[v.name]);
          if (error) {
            errors[v.name] = error;
          }
        }
      });
    }

    setState((prev) => ({
      ...prev,
      errors,
      isValid: Object.keys(errors).length === 0,
    }));

    return errors;
  }, [registeredInputs, state.values, workflowSchema, validateFieldInternal]);

  const getFieldError = useCallback(
    (fieldName: string): string | null => {
      return state.errors[fieldName] || null;
    },
    [state.errors]
  );

  const clearFieldError = useCallback((fieldName: string) => {
    setState((prev) => {
      const newErrors = { ...prev.errors };
      delete newErrors[fieldName];
      return {
        ...prev,
        errors: newErrors,
        isValid: Object.keys(newErrors).length === 0,
      };
    });
  }, []);

  // ========================================================================
  // Direct Submission to Data Service (bypasses workflow)
  // ========================================================================
  const handleDirectSubmit = useCallback(async (): Promise<FormSubmissionResult> => {
    if (!properties.groupId || !properties.schemaId) {
      return {
        success: false,
        errors: [
          {
            code: "CONFIG_ERROR",
            message: "Missing groupId or schemaId for direct submission",
          },
        ],
      };
    }

    setState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const { dataApi } = await import("@/services/api/dataApi");

      const response = await dataApi.createRecord(
        properties.groupId,
        properties.schemaId,
        state.values,
        { source: "form", tags: ["form-submission"] }
      );

      const result: FormSubmissionResult = {
        success: true,
        executionId: response.insertedId,
        status: "completed",
        message: "Data saved successfully",
      };

      // Reset form if configured
      if (properties.resetOnSubmit) {
        resetForm();
      }

      onSubmitSuccess?.(result);
      return result;
    } catch (error: unknown) {
      // Log full error details for debugging
      const apiError = error as { code?: string; message?: string; details?: unknown; status?: number };
      console.error('[FormContext] Direct submit error:', {
        errorCode: apiError.code,
        errorMessage: apiError.message,
        errorDetails: apiError.details,
        errorStatus: apiError.status,
        groupId: properties.groupId,
        schemaId: properties.schemaId,
        valueKeys: Object.keys(state.values),
        values: state.values,
      });

      // Extract error details from API error object
      const errorMessage = apiError.message || (error instanceof Error ? error.message : "Failed to save data");
      const errorCode = apiError.code || "SAVE_ERROR";

      const errorResult: FormSubmissionResult = {
        success: false,
        errors: [
          {
            code: errorCode,
            message: errorMessage,
          },
        ],
      };

      onSubmitError?.(error instanceof Error ? error : new Error(errorMessage));
      return errorResult;
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  }, [
    properties.groupId,
    properties.schemaId,
    properties.resetOnSubmit,
    state.values,
    onSubmitSuccess,
    onSubmitError,
    // Note: resetForm is defined below but this is safe because
    // the callback is not called during definition
  ]);

  // ========================================================================
  // Form Submission (routes to direct or workflow based on mode)
  // ========================================================================
  const handleSubmit = useCallback(async (): Promise<FormSubmissionResult> => {
    // Validate form first
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      return {
        success: false,
        errors: Object.entries(errors).map(([field, message]) => ({
          field,
          code: "VALIDATION_ERROR",
          message,
        })),
      };
    }

    // Check submission mode - direct to Data Service or via Workflow
    if (properties.submitMode === "direct" && !properties.workflowId) {
      return handleDirectSubmit();
    }

    // Auto-detect: If no workflowId but schemaId is set, use direct submission
    // This handles forms created with schema binding but no explicit workflow
    if (!properties.workflowId && properties.schemaId) {
      console.log('[FormContext] Auto-detecting direct submission mode (schemaId set, no workflowId)');
      return handleDirectSubmit();
    }

    // Default: Workflow submission
    // Check if workflow is configured
    if (!properties.workflowId) {
      return {
        success: false,
        errors: [
          {
            code: "NO_WORKFLOW",
            message: "No workflow configured for this form",
          },
        ],
      };
    }

    setState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const { workflowApi } = await import("@/services/api/workflowApi");

      const response = await workflowApi.post<{
        success: boolean;
        execution?: {
          id: string;
          status: string;
        };
        error?: string;
        code?: string;
        details?: string;
      }>(`/api/workflows/${properties.workflowId}/execute`, {
        triggerData: {
          ...state.values,
          // Include metadata for edit mode
          ...(properties.mode === "edit" &&
            properties.recordId && {
              _recordId: properties.recordId,
              _schemaId: properties.schemaId,
              _mode: "edit",
            }),
        },
        priority: 1,
      });

      if (response.success && response.execution) {
        const result: FormSubmissionResult = {
          success: true,
          executionId: response.execution.id,
          status: response.execution.status,
          message: "Form submitted successfully",
        };

        // Reset form if configured
        if (properties.resetOnSubmit) {
          resetForm();
        }

        onSubmitSuccess?.(result);
        return result;
      }

      const errorResult: FormSubmissionResult = {
        success: false,
        errors: [
          {
            code: response.code || "SUBMISSION_FAILED",
            message: response.error || response.details || "Submission failed",
          },
        ],
      };

      onSubmitError?.(new Error(errorResult.errors?.[0]?.message));
      return errorResult;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      const errorResult: FormSubmissionResult = {
        success: false,
        errors: [
          {
            code: "SUBMISSION_ERROR",
            message: errorMessage,
          },
        ],
      };

      onSubmitError?.(error instanceof Error ? error : new Error(errorMessage));
      return errorResult;
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  }, [
    validateForm,
    handleDirectSubmit,
    properties.submitMode,
    properties.workflowId,
    properties.mode,
    properties.recordId,
    properties.schemaId,
    properties.resetOnSubmit,
    state.values,
    onSubmitSuccess,
    onSubmitError,
  ]);

  // ========================================================================
  // Register with Form Registry (for button-triggered submissions)
  // ========================================================================
  // Use a ref to track the registration version and avoid race conditions
  // with React's strict mode and dynamic imports
  const registrationRef = useRef<{ formId: string; version: number }>({ formId: '', version: 0 });

  // Use a ref for handleSubmit to avoid infinite loop in registration useEffect
  // handleSubmit changes whenever state.values changes, but we want the latest version
  // to be called when submit is triggered, not re-register every time
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  useEffect(() => {
    // Increment version for this registration
    const currentVersion = ++registrationRef.current.version;
    registrationRef.current.formId = formId;

    // Import dynamically to avoid circular dependencies
    import("@/services/form/FormSubmissionService").then(({ formRegistry }) => {
      // Register a wrapper that calls the ref to get the latest handleSubmit
      formRegistry.register(formId, () => handleSubmitRef.current());
    });

    return () => {
      // Only unregister if this is still the current registration
      // This prevents race conditions where an old cleanup unregisters a newer registration
      const capturedFormId = formId;
      const capturedVersion = currentVersion;

      import("@/services/form/FormSubmissionService").then(({ formRegistry }) => {
        // Check if this cleanup is for the current registration
        if (registrationRef.current.formId === capturedFormId &&
            registrationRef.current.version > capturedVersion) {
          // A newer registration exists, don't unregister
          console.log(`[FormRegistry] Skipping unregister for ${capturedFormId} (newer version exists)`);
          return;
        }
        formRegistry.unregister(capturedFormId);
      });
    };
  }, [formId]); // Only depend on formId, use ref for handleSubmit

  // ========================================================================
  // Reset Form
  // ========================================================================
  const resetForm = useCallback(() => {
    // Get default values from workflow schema
    const defaultValues: Record<string, unknown> = {};
    if (workflowSchema) {
      workflowSchema.variables.forEach((v) => {
        if (v.defaultValue !== undefined) {
          defaultValues[v.name] = v.defaultValue;
        }
      });
    }

    setState({
      ...INITIAL_FORM_STATE,
      values: defaultValues,
    });
  }, [workflowSchema]);

  // ========================================================================
  // Context Value
  // ========================================================================
  const contextValue = useMemo<FormContextValue>(
    () => ({
      state,
      workflowSchema,
      schemaDefinition,
      isLoading,
      formId,
      mode: properties.mode,

      registerInput,
      unregisterInput,

      setValue,
      getValue,
      setTouched,

      validateField,
      validateForm,
      getFieldError,
      clearFieldError,

      handleSubmit,
      resetForm,
    }),
    [
      state,
      workflowSchema,
      schemaDefinition,
      isLoading,
      formId,
      properties.mode,
      registerInput,
      unregisterInput,
      setValue,
      getValue,
      setTouched,
      validateField,
      validateForm,
      getFieldError,
      clearFieldError,
      handleSubmit,
      resetForm,
    ]
  );

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
};

export default FormContext;
